/**
 * Client-side route construction.
 *
 * Kept in one place so the URL printed on a QR code, the QR the customer shows
 * at pickup, and the links the merchant copies all agree — and so the two apps
 * can be re-pointed at a native deep-link scheme without hunting for literals.
 */

/** Customer app: the storefront for a restaurant slug. */
export function menuPath(slug: string): string {
  return `/r/${encodeURIComponent(slug)}`;
}

/** Customer app: entry point encoded in a printed table QR. */
export function tablePath(token: string): string {
  return `/t/${encodeURIComponent(token)}`;
}

/** Customer app: live tracking for one order. */
export function orderPath(orderId: string): string {
  return `/order/${encodeURIComponent(orderId)}`;
}

/** Customer app: recovery entry point when only the short code is known. */
export function orderCodePath(code: string): string {
  return `/order/code/${encodeURIComponent(code)}`;
}

/** Customer app: the customer's own order history. */
export const HISTORY_PATH = '/orders';

/** Merchant app paths. */
export const MERCHANT_PATHS = {
  login: '/login',
  signup: '/signup',
  onboarding: '/onboarding',
  dashboard: '/',
  orders: '/orders',
  menu: '/menu',
  tables: '/tables',
  hours: '/hours',
  settings: '/settings',
  /**
   * The kitchen's own capacity, worked out from the rounds it has cooked.
   *
   * A page rather than a settings card: the two knobs it is about — how many
   * orders the kitchen works on at once, and how long a round covers — are set in
   * settings, but reading them against a model of the queue is a different job
   * from changing them, and the answer is a page's worth of measurement beside a
   * what-if.
   */
  capacity: '/capacity',
} as const;

/**
 * Absolute URL for a table QR payload, for printing or sharing.
 *
 * @param origin The web origin, for example `https://order.example.com`.
 * @param token A signed `YMT1` table token.
 * @returns A fully qualified URL.
 */
export function tableUrl(origin: string, token: string): string {
  return new URL(tablePath(token), origin).toString();
}

/**
 * Absolute URL for the customer's tracking page.
 *
 * @param origin The web origin.
 * @param orderId The order id.
 * @returns A fully qualified URL.
 */
export function orderUrl(origin: string, orderId: string): string {
  return new URL(orderPath(orderId), origin).toString();
}

/**
 * The Google Maps link for an address.
 *
 * The universal form rather than an app scheme: on a phone the operating system
 * hands this to the Maps app, and anywhere else it opens the web map, so one
 * string covers every device and nothing has to detect whether the app is
 * installed and fall back when it is not.
 *
 * @param address The restaurant's collection address.
 * @returns A Maps URL that searches for it.
 */
export function mapsUrl(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.trim())}`;
}

/**
 * A dialable `tel:` URI for a stored number.
 *
 * Stored numbers keep their spacing — `+65 6221 8899` — and a space is not one
 * of the visual separators RFC 3966 allows, so the punctuation comes off before
 * the number becomes a URI. The leading `+` is what makes it dialable from
 * another country and is kept.
 *
 * @param phone The number as the restaurant stored it.
 * @returns A `tel:` URI.
 */
export function telUrl(phone: string): string {
  return `tel:${phone.replace(/[\s().-]/g, '')}`;
}
